import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  DialogContent,
  Link,
  Box,
  Tooltip,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LabelIcon from '@mui/icons-material/Label';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CodeIcon from '@mui/icons-material/Code';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import Warning from '@mui/icons-material/Warning';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EventNoteIcon from '@mui/icons-material/EventNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import * as Icons from '@mui/icons-material';
import { getTheme } from 'config/theme';
import LocationMap from 'components/organisms/locationMap/view'
import { useProfile } from 'context';
import { updateBooking } from 'api';

const BookingDetail = ({
  booking,
  view,
  handleBookingAction,
  navigateVenue
}) => {
  const { profile } = useProfile();
  // Prepare Google Maps link
  const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(booking?.venue?.address)}`;
  const [theme] = useState(getTheme(null));
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Amenities with icons
  const getIconComponent = (iconName) => {
    return Icons[iconName] || Icons.Help;
  };

  const typeMapping = {
    HD: 'Hot Desk',
    PR: 'Private Office',
    MR: 'Meeting Room',
    EV: 'Event',
    OT: 'Other'
  }

  const statusMapping = {
    AL: 'All',
    AC: 'Active',
    PP: 'Pending Payment',
    PC: 'Pending Confirmation',
    PN: 'Pending',
    PB: 'Pre-Booked',
    PR: 'Prepaid',
    CA: 'Cancelled',
    FN: 'Finished',
    RJ: 'Rejected'
  };

  const getDayName = (day) => {
    switch (day) {
      case 0:
        return 'Monday'
      case 1:
        return 'Tuesday'
      case 2:
        return 'Wednesday'
      case 3:
        return 'Thursday'
      case 4:
        return 'Friday'
      case 5:
        return 'Saturday'
      case 6:
        return 'Sunday'
      default:
        return ''
    }
  }

  // Add this function to check if the booking can be cancelled by the user
  const canCancelBooking = () => {
    // Staff can cancel anytime
    if (profile.staff?.is_staff) return true;

    // Only active bookings can be cancelled
    if (booking?.status !== 'AC') return false;

    // Check 24-hour notice for non-staff
    const bookingStartTime = new Date(booking.planned_start_day + 'T' + booking.planned_start_time);
    const now = new Date();
    const hoursDifference = (bookingStartTime - now) / (1000 * 60 * 60);

    return hoursDifference >= 24;
  };

  // Update the handleCancelBooking function
  const handleCancelBooking = async () => {
    if (!canCancelBooking() && !profile.staff?.is_staff) {
      setError('Bookings can only be cancelled with at least 24 hours notice.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call API to cancel the booking
      const response = await updateBooking({
        slug: booking.slug,
        status: 'CA'
      });

      if (response.status === 'CA') {
        // Create a modified booking object with updated status
        const updatedBooking = {
          ...booking,
          status: 'CA'
        };

        // Update booking in parent component if handleBookingAction exists
        if (handleBookingAction) {
          handleBookingAction(booking.slug, 'update', updatedBooking);
        }

        // Hide the cancellation section
        setShowCancelConfirmation(false);
      }

      setIsLoading(false);
    } catch {
      setError('Failed to cancel booking. Please try again.');
      setIsLoading(false);
    }
  };


  const bookingPrice = booking?.price && booking?.vat_amount
    ? (parseFloat(booking.price)).toFixed(2) //  + parseFloat(booking.vat_amount)
    : 'Free';
  const formattedCreatedDate = booking?.create_date ? new Date(booking.create_date).toLocaleDateString('en-GB') : '-';
  const formattedDate = booking?.planned_start_day ? new Date(booking.planned_start_day).toLocaleDateString('en-GB') : '-';
  const formattedStartTime = booking?.planned_start_time ? booking.planned_start_time.slice(0, -3) : '-';
  const formattedEndTime = booking?.planned_end_time ? booking.planned_end_time.slice(0, -3) : '-';
  return (
    <DialogContent
      sx={{
        padding: 0,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px'
        }
      }}
    >
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
        sx={{ padding: { xs: '12px', md: '25px' } }}
      >
        {/* Booking Information Section */}
        {view !== 'space' && <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
            <Typography variant="h4" gutterBottom color={'primary'}>
              Booking Details
            </Typography>

            <Grid container spacing={2}>
              {[
                {
                  icon: <CalendarTodayIcon />,
                  label: 'Date',
                  value: formattedDate,
                  xs: '12',
                  sm: '6',
                  md: '4',
                },
                {
                  icon: <AccessTimeIcon />,
                  label: 'Time',
                  value: `${formattedStartTime} - ${formattedEndTime}`,
                  xs: '12',
                  sm: '6',
                  md: '4',
                },
                {
                  icon: <LabelIcon />,
                  label: 'Type',
                  value: typeMapping[booking?.booking_type],
                  xs: '12',
                  sm: '6',
                  md: '4',
                },
                {
                  icon: <MonetizationOnIcon />,
                  label: 'Price (VAT exc.)',
                  value: `£${bookingPrice}`,
                  xs: '12',
                  sm: '6',
                  md: '4',
                },
                {
                  icon: <BookmarkIcon />,
                  label: 'Status',
                  value: statusMapping[booking?.status],
                  xs: '12',
                  sm: '6',
                  md: '4',
                },
                {
                  icon: <CodeIcon />,
                  label: 'Booking Code',
                  value: booking?.code,
                  xs: '12',
                  sm: '6',
                  md: '4',
                },
                {
                  icon: <StorefrontIcon />, // More venue-specific than LocationOn
                  label: 'Venue',
                  value: booking?.venue?.name,
                  xs: '12',
                  sm: '6',
                  md: '4',
                  onClick: () => booking?.venue?.slug && navigateVenue(booking.venue.slug)
                },
                {
                  icon: <AccountCircleIcon />, // More personal than LocationOn
                  label: 'Member',
                  value: booking?.member?.user?.first_name + ' ' + booking?.member?.user?.last_name,
                  xs: '12',
                  sm: '6',
                  md: '4',
                },
                {
                  icon: <EventNoteIcon />, // Better for date creation
                  label: 'Created on',
                  value: formattedCreatedDate,
                  xs: '12',
                  sm: '6',
                  md: '4',
                }
              ].map((item, index) => (
                <Grid item key={index} xs={item.xs} sm={item.sm} md={item.md}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      padding: 1,
                      backgroundColor: 'background.paper',
                      borderRadius: 1,
                      cursor: item.onClick ? 'pointer' : 'default',
                      '&:hover': {
                        ...(item.onClick && {
                          backgroundColor: 'action.hover',
                        }),
                      },
                    }}
                    onClick={item.onClick}
                  >
                    {item.icon}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body1"
                        color={item.onClick ? 'primary' : 'text.primary'}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                    {item.onClick && (
                      <Box sx={{ marginLeft: 'auto' }}>
                        <OpenInNewIcon fontSize="small" color="action" />
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>}

        {booking?.status === 'PC' && profile.staff?.is_staff && (
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ padding: 3, borderRadius: 2, marginTop: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  minHeight: '60px'
                }}
              >
                <Button
                  onClick={() => handleBookingAction(booking.slug, 'accept')}
                  fullWidth={true}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'white',
                    color: 'success.main',
                    border: '1px solid',
                    borderColor: 'success.main',
                    borderRadius: '20px',
                    padding: '6px 16px',
                    maxWidth: { xs: '100%', sm: '240px' },
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'white',
                      borderColor: 'success.main',
                    }
                  }}
                  startIcon={<Check sx={{ fontSize: '1.1rem' }} />}
                >
                  Confirm booking
                </Button>

                <Button
                  onClick={() => handleBookingAction(booking.slug, 'reject')}
                  fullWidth={true}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'white',
                    color: 'error.main',
                    border: '1px solid',
                    borderColor: 'error.main',
                    borderRadius: '20px',
                    padding: '6px 16px',
                    maxWidth: { xs: '100%', sm: '240px' },
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'white',
                      borderColor: 'error.main',
                    }
                  }}
                  startIcon={<Close sx={{ fontSize: '1.1rem' }} />}
                >
                  Decline booking
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Add this right after the Booking Details section */}
        {booking?.status === 'CA' && (
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                padding: 3,
                borderRadius: 2,
                backgroundColor: 'success.lighter',
                border: '1px solid',
                borderColor: 'success.light'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Icons.CheckCircle sx={{ color: 'success.main', mr: 2, fontSize: '1.5rem' }} />
                <Box>
                  <Typography variant="h6" color="success.dark" gutterBottom>
                    Booking Successfully Cancelled
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {booking?.price && parseFloat(booking.price) > 0 ?
                      "A refund will be processed to your original payment method." :
                      "Your booking has been cancelled."}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}
        {/* Cancellation Section - Only show for active bookings or staff */}
        {(booking?.status === 'AC' || profile.staff?.is_staff) && (
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                padding: 3,
                borderRadius: 2,
                border: showCancelConfirmation ? '1px solid' : 'none',
                borderColor: 'error.light',
                backgroundColor: showCancelConfirmation ? 'error.lighter' : 'background.paper'
              }}
            >
              {!showCancelConfirmation ? (
                // Initial view with cancel button
                <Box>
                  <Typography variant="h6" gutterBottom color="text.primary">
                    Need to cancel this booking?
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icons.InfoOutlined sx={{ color: 'info.main', mr: 1, fontSize: '1rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      {profile.staff?.is_staff
                        ? "As a staff member, you can cancel this booking at any time."
                        : "You can cancel this booking up to 24 hours before the start time."}
                    </Typography>
                  </Box>

                  {!canCancelBooking() && !profile.staff?.is_staff && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Warning sx={{ color: 'warning.main', mr: 1, fontSize: '1rem' }} />
                      <Typography variant="body2" color="warning.main">
                        This booking is within 24 hours and cannot be cancelled online.
                        Please contact the venue directly if you need assistance.
                      </Typography>
                    </Box>
                  )}

                  <Button
                    onClick={() => setShowCancelConfirmation(true)}
                    disabled={!canCancelBooking() && !profile.staff?.is_staff}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'error.main',
                      border: '1px solid',
                      borderColor: 'error.main',
                      borderRadius: '20px',
                      padding: '6px 16px',
                      '&:hover': {
                        backgroundColor: 'error.lighter',
                      },
                      '&.Mui-disabled': {
                        borderColor: 'action.disabledBackground',
                        color: 'action.disabled'
                      }
                    }}
                    startIcon={<Icons.Cancel sx={{ fontSize: '1.1rem' }} />}
                  >
                    Cancel Booking
                  </Button>
                </Box>
              ) : (
                // Confirmation view
                <Box>
                  <Typography variant="h6" gutterBottom color="error.main">
                    Confirm Cancellation
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to cancel this booking? This action cannot be undone.
                  </Typography>

                  {booking?.price && booking?.price > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MonetizationOnIcon sx={{ color: 'info.main', mr: 1, fontSize: '1rem' }} />
                      <Typography variant="body2" color="text.secondary">
                        If eligible, a refund will be processed automatically to the original payment method.
                      </Typography>
                    </Box>
                  )}

                  {error && (
                    <Typography variant="body2" color="error" sx={{ mt: 2, mb: 2 }}>
                      {error}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      onClick={() => setShowCancelConfirmation(false)}
                      disabled={isLoading}
                      sx={{
                        borderRadius: '20px',
                        padding: '6px 16px',
                      }}
                    >
                      Keep Booking
                    </Button>

                    <Button
                      onClick={handleCancelBooking}
                      disabled={isLoading}
                      sx={{
                        backgroundColor: 'error.main',
                        color: 'white',
                        borderRadius: '20px',
                        padding: '6px 16px',
                        '&:hover': {
                          backgroundColor: 'error.dark',
                        },
                      }}
                      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Icons.Cancel />}
                    >
                      Confirm Cancellation
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        )}


        {view == 'full' &&
          <Grid item xs={12} sx={{ marginTop: 3 }}>
            <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
              <Typography variant="h4" gutterBottom color={'primary'}>
                {booking?.venue?.name}
              </Typography>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  {booking?.venue?.contact_phone_1 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                    <PhoneIcon />
                    <Typography variant="body2">
                      {booking?.venue?.contact_phone_1}
                    </Typography>
                  </Box>}

                  {booking?.venue?.contact_email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                    <EmailIcon />
                    <Typography variant="body2">
                      {booking?.venue?.contact_email}
                    </Typography>
                  </Box>}
                  {/* Access Information */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                    <LocationOnIcon />
                    <Typography variant="body2">
                      {booking?.venue?.single_address}
                    </Typography>
                  </Box>
                  <div dangerouslySetInnerHTML={{ __html: booking?.venue?.access }} />

                  {/* Placeholder for Map */}
                  <Box
                    sx={{
                      height: 250,
                      backgroundColor: 'grey.200',
                      marginTop: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: 2
                    }}
                  >

                    {booking?.venue && booking?.venue?.coordinates && booking?.venue?.coordinates[0] && booking?.venue?.coordinates[1] && (
                      <LocationMap
                        latitude={booking?.venue?.coordinates[0]}
                        longitude={booking?.venue?.coordinates[1]}
                        name={booking?.venue?.name}
                        googleMapsUrl={booking?.venue?.google_maps_url}
                        height={250}
                        showButton={false}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* Venue Name and Address */}


                  <Link
                    href={googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'block', marginBottom: 2 }}
                  >
                    {booking?.venue?.address}
                  </Link>

                  <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
                    Opening Times
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {[...Array(7).keys()].map(day => {
                          const openTime = booking?.venue?.open_time?.find(o => o.day === day);
                          const formattedOpen = openTime?.time_start?.split(':').slice(0, 2).join(':') || 'Closed';
                          const formattedClose = openTime?.time_end?.split(':').slice(0, 2).join(':') || '';
                          return (
                            <TableRow key={day}>
                              <TableCell>{getDayName(day)}</TableCell>
                              <TableCell>{formattedOpen} {formattedClose && `- ${formattedClose}`}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
                    Amenities
                  </Typography>
                  <Grid container spacing={2} sx={{ margin: '0px 10px' }}>
                    {booking?.venue?.venue_services?.map((amenity) => {
                      const IconComponent = getIconComponent(amenity.icon);
                      return (
                        <Grid
                          item
                          key={amenity.slug}
                          xs={6}
                          sm={6}
                          style={{ display: 'flex', alignItems: 'center', padding: '8px 0px' }}
                        >
                          <IconComponent sx={{ fontSize: 18, marginRight: '4px', color: theme.icons_lite.main }} />
                          <Tooltip title={amenity.description} placement="top">
                            <Typography
                              noWrap
                              sx={{
                                fontSize: '0.7rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {amenity.name}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>

                {/* Right Column */}

              </Grid>

              {/* Venue Description */}
              <Divider sx={{ marginY: 2 }} />
              <Typography variant="subtitle1">
                Venue Description
              </Typography>
              <Typography variant="body2">
                <div dangerouslySetInnerHTML={{ __html: booking?.venue?.description }} />
              </Typography>
            </Paper>
          </Grid>
        }
      </Grid>
    </DialogContent>
  );
};

export default BookingDetail;